
import * as Sentry from "@sentry/vue";
import _ from "lodash";
import Vue from "vue";
import { mapActions, mapGetters, mapMutations } from "vuex";
import { getGoogleMapsLoader } from "~/assets/utils/googleMapsLoader"; // singleton Google Maps Loader
import areAllPropertiesFalse from "~/assets/utils/areAllPropertiesFalse";
import { extractCheckTags, extractPillTag } from "~/utils/guidanceTagParser";
import checkIfDetailUpdated from "~/assets/utils/checkIfDetailUpdated";
import { getCarrierRestriction } from "~/assets/utils/carrierAddressGuards";
import highlightsDiff from "~/assets/utils/highlightsDiff";
import Modal from "~/components/Modal.vue";
import Notice from "~/components/Notice.vue";
import NoticeEntity from "~/entities/Notice";
import LargeSelect from "~/components/form/LargeSelect.vue";
import SmallRoundedSelect from "~/components/form/SmallRoundedSelect.vue";
import BoxCaseForm from "~/components/ship/BoxCaseForm.vue";
import RecentSearch from "~/components/ship/RecentSearch.vue";
import StageForm from "~/components/ship/StageForm.vue";
import { AVSSubStatus, AVSStatus } from "~/entities/AVS";
import Box from "~/entities/Box";
import { Details, StageDetails } from "~/entities/Details";
import {
  Category,
  Country,
  Insurance,
  Preset,
  Purpose,
  State,
  UserPreset,
} from "~/entities/Response/FormData";
import SavedAddress from "~/entities/SavedAddress";
import Trip from "~/entities/Trip";
import Units from "~/entities/Units";
import Validation from "~/entities/Validation";
import ValidationSearch from "~/entities/ValidationModules/ValidationSearch";

interface StageDetailsI {
  from: Details;
  to: Details;
}

export default Vue.extend({
  name: "ShipSearch",
  components: {
    SmallRoundedSelect,
    LargeSelect,
    StageForm,
    BoxCaseForm,
    RecentSearch,
    Notice,
    Modal,
  },
  layout: "WithServices",
  data() {
    return {
      validation: new Validation(new ValidationSearch()),
      stepNumber: 1,
      googleObj: null as any,
      outShip: this.$route.name !== "ship" ? true : (false as boolean),
      refreshView: false as boolean,
      defaultShipMode: [
        {
          id: 1,
          label: "One-Way",
        },
        {
          id: 2,
          label: "Round-Trip",
        },
        {
          id: 3,
          label: "Multi-City",
        },
      ],
      packageNumber: [
        {
          id: 1,
          label: "1 Package",
        },
      ],
      units: [
        {
          id: 1,
          label: "in / lbs",
          units: new Units({
            weight: "lb",
            length: "in",
          }),
        },
        {
          id: 2,
          label: "cm / kgs",
          units: new Units({
            weight: "kg",
            length: "cm",
          }),
        },
      ],
      packagePresets: [] as Preset[],
      userPresets: [] as UserPreset[],
      userAddresses: [] as SavedAddress[],
      packageCategories: [] as Category[],
      insuranceOptions: [] as Insurance[],
      packagePurpose: [] as Purpose[],
      purposeSelected: null as any,
      // BIK-2140: legacy (flag-OFF) source for the allowedCountries prop — still assigned
      // once at the end of fetch(). The flag-ON path uses the computed below instead, which
      // tracks the store getter so the prop updates when the countries request resolves.
      allowedCountriesLegacy: [] as Country[],
      allowedStates: [] as State[],
      currentUnit: {} as Units,
      currentShipMode: {
        id: 1,
        label: "One-Way",
      } as { id: number; label: string },
      currentCountries: {} as any,
      rendered: false as boolean,
      tripsLimit: 6,
      isOpen: false as boolean,
      avsChecked: false as boolean,
      stageDetails: [] as StageDetailsI[],
      updatedDetails: [] as StageDetailsI[],
      failedAvsEvent: [] as { from: boolean; to: boolean }[],
      changedFlags: [] as { from: any; to: any }[],
      disabled: false as boolean,
      guidanceAlerts: {} as Record<string, NoticeEntity | null>,
      nudgeConfirmations: {} as Record<string, boolean>,
      debouncedTiers: {} as Record<number, { state: string; tier: string; metric: string }>,
      debouncedAlerts: {} as Record<number, Array<{ state: string; tier: string; metric: string }>>,
      debounceDimensions: null as any,
    };
  },
  async fetch() {
    const _this = this as any;
    if (_this.$config?.avsRawPasteDirect === true) {
      // BIK-2140: these six were awaited one after another, so the countries/states lists —
      // last in the chain — could not land until four unrelated requests had finished. The
      // preferred-address prefill starts from an immediate watcher racing a single request,
      // so it reliably won and resolved country/state against still-empty lists. Fire them
      // together instead: the lists now arrive as early as the network allows.
      // Each request is wrapped so one failure cannot reject the batch (same tolerance as
      // Promise.allSettled, without depending on ES2020). The store actions already swallow
      // their own errors and return the cached getter, so a failure here is a no-op anyway.
      const settle = (p: Promise<unknown>) => Promise.resolve(p).catch(() => undefined);
      await Promise.all([
        settle(_this.fetchPackageCategories()),
        settle(_this.fetchPackagePurpose()),
        settle(_this.fetchPackagePresets()),
        settle(_this.fetchInsuranceOptions()),
        settle(_this.fetchAllowedCountries()),
        settle(_this.fetchAllowedStates()),
      ]);
    } else {
      await _this.fetchPackageCategories();
      await _this.fetchPackagePurpose();
      await _this.fetchPackagePresets();
      await _this.fetchInsuranceOptions();
      await _this.fetchAllowedCountries();
      await this.fetchAllowedStates();
    }

    _this.packageCategories = _this.categories
      ?.map((el: Category, index: number) => {
        return {
          id: index as number,
          entryId: el.entryId as string,
          code: el.code as number,
          label: el.display as string,
          sortOrder: el.sortOrder as number,
        };
      })
      .sort((a, b) => {
        return a.sortOrder < b.sortOrder
          ? -1
          : a.sortOrder > b.sortOrder
          ? 1
          : 0;
      });
    _this.packagePresets = _this.presets
      ?.map((el: Preset) => {
        return {
          type: el.type as string,
          label: el.name as string,
          length: el.length as number,
          width: el.width as number,
          height: el.height as number,
          weight: el.weight as number,
          isEbike: el.isEbike as boolean,
          isExact: el.isExact as boolean,
          linearUnit: el.linearUnit as string,
          weightUnit: el.weightUnit as string,
          sortOrder: el.sortOrder as number,
        };
      })
      .sort((a, b) => {
        return a.sortOrder < b.sortOrder
          ? -1
          : a.sortOrder > b.sortOrder
          ? 1
          : 0;
      });
    _this.packagePurpose = _this.purposes
      ?.map((el: Purpose, index: number) => {
        return {
          id: index as number,
          entryId: el.entryId as string,
          code: el.code as number,
          label: el.display as string,
          sortOrder: el.sortOrder as number,
        };
      })
      .sort((a, b) => {
        return a.sortOrder < b.sortOrder
          ? -1
          : a.sortOrder > b.sortOrder
          ? 1
          : 0;
      });
    _this.insuranceOptions = _this.options
      ?.map((el: Insurance, index: number) => {
        return {
          id: index as number,
          entryId: el.entryId as string,
          label: el.display as string,
          coverage: el.coverage as number,
          cost: el.cost as number,
          sortOrder: el.sortOrder as number,
          afterText: el.afterText as string
        };
      })
      .sort((a, b) => {
        return a.coverage < b.coverage ? -1 : a.coverage > b.coverage ? 1 : 0;
      });
    _this.allowedCountriesLegacy = _this.countries
      ?.map((el: Country) => {
        return {
          code: el.code as string,
          name: el.name as string,
          active: el.active as boolean,
          id: el.code as string,
          label: el.name as string,
        };
      })
      .filter((value) => value.active);
  },
  computed: {
    // Vuex getters
    ...mapGetters({
      activeNotices: "errorsBag/activeNotices" as any,
      alertResponses: "errorsBag/alertResponses" as any,
      loading: "loading",
      session: "user/session",
      boxes: "ship/boxes",
      trips: "ship/trips",
      currentUnitStore: "ship/units",
      details: "ship/details",
      purpose: "ship/purpose",
      ratesAreValid: "ship/ratesAreValid",
      eventInputOptions: "ship/eventInputOptions",
      shipMode: "ship/shipMode",
      currentEvent: "eventShipping/currentEvent",

      // formData
      categories: "formData/packageCategories",
      presets: "formData/packagePresets",
      purposes: "formData/packagePurpose",
      options: "formData/insuranceOptions",
      countries: "formData/allowedCountries",
      states: "formData/allowedStates",
      hasAdvancedInStep: "ship/hasAdvancedInStep",
      intShipStatus:"intShipStatus",
    }),
    // BIK-2135: read by ValidationSearch (a plain TS class with no Nuxt
    // context) through `this.component` to gate the carrier-restricted
    // address guard.
    avsRawPasteDirect(): boolean {
      return (this as any).$config?.avsRawPasteDirect === true;
    },
    // BIK-2140: the allowed-countries prop used to be a data field written once, at the end
    // of fetch(). That made it blind to the store: if anything downstream needed the list
    // before fetch() finished (the preferred-address prefill does), it saw an empty array
    // and silently degraded country/state to raw strings, with nothing to recover them.
    // Deriving it from the getter instead means the prop repopulates the moment the request
    // resolves, which is what AddressDropdown's re-resolve watcher hangs off.
    // Same map+filter as the legacy assignment, so the option shape is unchanged.
    allowedCountries(): Country[] {
      if (!(this as any).avsRawPasteDirect) {
        return (this as any).allowedCountriesLegacy;
      }
      return ((this as any).countries ?? [])
        .map((el: Country) => {
          return {
            code: el.code as string,
            name: el.name as string,
            active: el.active as boolean,
            id: el.code as string,
            label: el.name as string,
          };
        })
        .filter((value: Country) => value.active);
    },
    boxesCount(): number {
      return this.boxes ? this.boxes.length : (0 as number);
    },
    currentUnitIndex(): number {
      try {
        if (this.units && this.currentUnit)
          return this.units.findIndex(
            (unit) =>
              unit.units.length === this.currentUnit.length &&
              unit.units.weight === this.currentUnit.weight
          );
        return 0;
      } catch (e) {
        return 0;
      }
    },
    shipTypeSelection(): number {
      if (this.outShip) {
        return this.currentShipMode && this.currentShipMode.id !== 0
          ? this.currentShipMode.id - 1
          : 0;
      } else if (this.shipMode && this.shipMode.id === 1) {
        return 0;
      } else {
        return this.shipMode && this.shipMode.id > 2 ? 2 : 1;
      }
    },
    allAvsChecked(): boolean {
      return this.details.every((detail) => {
        const result =
          detail.from.avsChecked === true && detail.to.avsChecked === true;
        return result;
      });
    },
    allNudgesConfirmed(): boolean {
      // Collect all checks across all boxes (nudge + guidance + protection)
      const allChecks: Array<{ id: string }> = [];
      for (let i = 0; i < this.boxes.length; i++) {
        allChecks.push(...this.getAllChecksForBox(i));
        allChecks.push(...this.getProtectionChecks(i));
      }
      if (allChecks.length === 0) return true;
      return allChecks.every((c: any) => this.nudgeConfirmations[c.id] === true);
    },
    hasOverMaxBox(): boolean {
      return this.boxes.some((_: any, index: number) => {
        // A box blocks Search if any active alert is a hard No: danger OVR (dim/weight),
        // Scicon (soft-sided cases we don't ship), or Minimal Size (below carrier minimums).
        return this.getDebouncedAlerts(index).some(
          a => a.state === 'danger' && (a.tier === 'ovr' || a.tier === 'scicon' || a.tier === 'min')
        );
      });
    },
    // BIK-2135: a FedEx/USPS/PO Box destination is a hard No — Bill asked that we
    // "not let folks enter" these, so the Search button is disabled outright
    // rather than letting the click bounce off validation. Mirrors hasOverMaxBox.
    // A UPS Store is intentionally NOT included: UPS delivers there, so it only
    // warrants a warning.
    hasCarrierRestrictedAddress(): boolean {
      if (!(this as any).avsRawPasteDirect) return false;
      return (this.details ?? []).some(
        (detail: any) =>
          !!getCarrierRestriction(detail?.from) ||
          !!getCarrierRestriction(detail?.to)
      );
    },
  },
  watch: {
    boxes: {
      deep: true,
      immediate: true,
      handler() {
        this.rendered = false;
        this.rendered = true;
        if (this.debounceDimensions) {
          this.debounceDimensions();
        }
      },
    },
    activeNotices() {
      this.rendered = false;
      this.rendered = true;
    },
    ratesAreValid(newVal) {
      if (newVal) {
        if (newVal.valid) {
          Sentry.addBreadcrumb({
            message: "Threshold: requested Rates are valid",
            data: { rates: JSON.stringify(newVal) },
            category: "search",
          });
        } else {
          this.setError(newVal, "rates");
          Sentry.addBreadcrumb({
            message: "Threshold: requested Rates are NOT valid",
            data: { rates: JSON.stringify(newVal) },
            category: "search",
          });
        }
      }
    },
    trip: {
      immediate: true,
      handler() {
        (this as any).validation.module.validateGoogleSelection();
      },
    },
    session: {
      deep: true,
      immediate: true,
      async handler(newVal: any) {
        if (newVal && newVal.sessionType) {
          // when season logs, call to fill the user presets
          await this.getUserPresets();
        } else if (newVal) {
          this.userPresets = [] as any[];
          this.userAddresses = [] as any[];
        }
      },
    },
    // BIK-2140: was `immediate: true`, which validated the form once on component init — before
    // the user had entered anything and before the async form data (countries/states, the
    // international-shipping status) had arrived. That first pass necessarily fails, setting
    // validationMade and painting every field red, and it raised the international-restriction
    // banner off a not-yet-loaded status.
    // It used to be invisible only because fetch()'s six sequential awaits delayed mount long
    // enough for the data to land first; parallelising them (this ticket) removed that accidental
    // cover and exposed a 1-2s red flash on entering Ship1. Validating an untouched form has no
    // user value, so drop the immediate pass — every real change still triggers the watcher.
    StageDetails: {
      deep: true,
      handler() {
        this.validation.Validate();
      },
    },
    // BIK-2140: the international-shipping status loads async and mounted() does not await it,
    // so the first validation pass usually runs before it exists. validateIsInternationalAndEUEnabled()
    // now skips while the value is undefined, which would otherwise mean the check never runs
    // at all — re-validate once it lands so the restriction is still enforced.
    // Only re-runs the international check, NOT the full Validate(): the status lands while the
    // form is still untouched, and a full pass there would fail on the empty fields and paint
    // them red — the very flash removing `immediate` above was meant to stop.
    // validateIsInternationalAndEUEnabled() now both raises and clears its own alert, so calling
    // it on its own is safe and cannot leave a duplicate behind.
    intShipStatus() {
      (this as any).validation.module.validateIsInternationalAndEUEnabled();
    },
    // listen for user request and fill the packagePresets and address
  },
  created() {
    this.debounceDimensions = _.debounce(() => {
      const tiers: Record<number, { state: string; tier: string; metric: string }> = {};
      const alerts: Record<number, Array<{ state: string; tier: string; metric: string }>> = {};
      if (this.boxes) {
        this.boxes.forEach((box: any, i: number) => {
          tiers[i] = this.getBoxThresholdTier(box);
          alerts[i] = this.getActiveAlerts(box);
        });
      }
      this.debouncedTiers = tiers;
      this.debouncedAlerts = alerts;
      this.recomputeNudges();
    }, 200);
    // Initial computation (no delay on first load)
    if (this.boxes) {
      const tiers: Record<number, { state: string; tier: string; metric: string }> = {};
      const alerts: Record<number, Array<{ state: string; tier: string; metric: string }>> = {};
      this.boxes.forEach((box: any, i: number) => {
        tiers[i] = this.getBoxThresholdTier(box);
        alerts[i] = this.getActiveAlerts(box);
      });
      this.debouncedTiers = tiers;
      this.debouncedAlerts = alerts;
    }
  },
  async mounted() {
    this.setLoading(true);
    this.setOutShip(this.outShip);
    this.initMap();
    for (let i = 2; i <= 25; i++) {
      this.packageNumber.push({
        id: i,
        label: i + " Packages",
      });
    } // set default package number
    this.currentShipMode =
      this.currentEvent && this.currentEvent.shipType
        ? this.defaultShipMode[this.currentEvent.shipType - 1]
        : this.defaultShipMode[0]; // set default ship mode
    setTimeout(() => {
      if (this.trips && !this.trips.length) {
        const trip: Trip[] = [];
        trip.push(new Trip());
        if (this.currentEvent && this.currentEvent.shipType) {
          for (let index = 0; index < this.currentEvent.shipType - 1; index++) {
            trip.push(new Trip());
          }
        }
        this.setTrips(trip);
      }
      if (typeof window !== "undefined" && window.dataLayer) {
        const route = this.$route.name;
        let event = null;
        if (
          route === "events-event" ||
          route === "event" ||
          route === "partners-event"
        ) {
          event = this.currentEvent?.name
            ? this.currentEvent.name
            : this.$route.params.event;
        }
        try {
          window.dataLayer.push({
            event: "page_impression",
            pagePath: this.$route.path,
            pageTitle: event
              ? "Ship - " + event + " - Search"
              : "Ship - Search",
            visitorType: this.$auth.loggedIn ? "customer" : "guest",
          });
        } catch (e) {
          Sentry.captureException(e);
        }
      }
    }, 100); // set default trips
    if (this.boxes && !this.boxes.length) {
      const boxes: Box[] = [];
      boxes.push(new Box());
      this.setBoxes(boxes);
    } // set default boxes
    // set default units
    if (this.units) {
      this.currentUnit = this.currentUnitStore ?? this.units[0].units; // set default unit
    } // set default units

    if (this.purpose && !this.purposeSelected) {
      this.purposeSelected = this.purpose;
    } // set default purpose
    // set component for validation module
    (this as any).validation.module.component = this;
    window.scrollTo(0, 0);

    // Used to restore searches that are coming from yoursearches page
    if (this.$route.params?.search) {
      const savedSearch = this.$route.params?.search;
      this.$set(this.$route.params, "search", false);
      this.onSearchResumed({ index: 0, search: savedSearch });
    }
    if (this.details?.length) {
      this.stageDetails = _.cloneDeep(this.details);
      this.updatedDetails = _.cloneDeep(this.details);
      if (this.hasAdvancedInStep) {
        this.validation.Validate();
      }
    }
    this.setLoading(false);
    this.getInternationalShippingStatus();
    await this.preloadTooltipNotices();
  },
  methods: {
    // Vuex actions
    ...mapMutations({
      setLoading: "setLoading",
      setBoxes: "ship/setBoxes",
      removeBoxes: "ship/removeBoxes",
      setTrips: "ship/setTrips",
      removeTrips: "ship/removeTrips",
      setUnits: "ship/setUnits",
      setPurpose: "ship/setPurpose",
      setShipMode: "ship/setShipMode",
      setRates: "ship/setRates",
      cleanBoxErrors: "ship/cleanBoxErrors",
      setBoxError: "ship/setBoxError",
      addTrip: "ship/addTrip",
      removeTripByIndex: "ship/removeTripByIndex",
      setOutShip: "ship/setOutShip",
      reassignActiveSearch: "user/reassignActiveSearch",
      setBoxProperty: "ship/setBoxProperty",
      setDetails: "ship/setDetails",
      setHasAdvancedInStep: "ship/setHasAdvancedInStep",
    }),
    ...mapActions({
      pushBox: "ship/pushBox",
      getRates: "ship/getRates",
      requestAlert: "errorsBag/requestAlert",
      getAlert: "errorsBag/getAlert",
      clearActiveNotices: "errorsBag/clearActiveNotices",
      clearAlertResponses: "errorsBag/clearAlertResponses",
      removeNotice: "errorsBag/removeNotice",
      addNotice: "errorsBag/addNotice",
      requestRates: "ship/requestRates",
      setStep: "ship/setStep",
      forgetSearch: "user/forgetSearch",
      resumeSearch: "user/resumeSearch",
      logRequest: "ship/logRequest",
      requestAVSCheck: "ship/requestAVSCheck",
      // formData
      requestUserPresets: "user/requestUserPresets",
      fetchPackageCategories: "formData/fetchPackageCategories",
      fetchPackagePresets: "formData/fetchPackagePresets",
      fetchPackagePurpose: "formData/fetchPackagePurpose",
      fetchInsuranceOptions: "formData/fetchInsuranceOptions",
      fetchAllowedCountries: "formData/fetchAllowedCountries",
      fetchAllowedStates: "formData/fetchAllowedStates",
      getInternationalShippingStatus: "getInternationalShippingStatus",
    }),
    async preloadTooltipNotices(): Promise<void> {
      // Fetch guidance alerts for threshold messaging
      const guidanceKeys = [
        'AlertNearSizeLimit',
        'AlertNearDimLimit',
        'AlertNearWeightLimit',
        'AlertAtSizeLimit',
        'AlertAtDimLimit',
        'AlertAtWeightLimit',
        'AlertOverSizeLimit',
        'AlertOverDimLimit',
        'AlertOverWeightLimit',
        'AlertNearLPS-L',
        'AlertNearLPS-LG',
        'AlertNearLPS-Cubic',
        'AlertAtLPS-L',
        'AlertAtLPS-LG',
        'AlertAtLPS-Cubic',
        'AlertHeavyPackage-Sturdy',
        'AlertProtectionTooLow',
        'SciconWarning',
        'AlertMinimalSize',
      ];
      const results = await Promise.all(guidanceKeys.map(k => this.getAlert({ name: k })));
      guidanceKeys.forEach((k, i) => { this.$set(this.guidanceAlerts, k, results[i]); });
    },
    highlightsDiff,
    async initMap() {
      // Initialize using singleton loader
      const gmapLoader = getGoogleMapsLoader({ apiKey: this.$config.mapsKey });
      return await gmapLoader
        .load()
        .then((google: any) => {
          this.googleObj = google;
        })
        .catch(() => {});
    },
    addTrip() {
      this.rendered = false;
      if (this.trips.length < this.tripsLimit) {
        this.setTrips([...this.trips, new Trip()]);
      }
      (this as any).validation.module.validateStages();
      this.rendered = true;
    },
    removeTrip(index?: number): void {
      this.setLoading(true);
      this.rendered = false;
      if (!index && index === undefined) {
        if (this.trips.length >= 3) {
          this.removeTrips();
          (this as any).validation.module.validateStages();
        } else {
          this.removeTrips();
        }
        setTimeout(() => {
          this.setLoading(false);
          this.rendered = true;
        }, 500);
        return;
      } else if (this.trips.length >= 3) {
        this.removeTripByIndex(index);
        (this as any).validation.module.validateStages();
        setTimeout(() => {
          this.setLoading(false);
          this.rendered = true;
        }, 500);
        return;
      }
      this.rendered = true;
    },
    onChangeUnits(unit: number) {
      this.currentUnit = this.units.find((el) => el.id === unit)!.units;
      this.setUnits(this.currentUnit);
    },
    onChangeShipMode(shipMode: number): void {
      this.rendered = false;
      this.currentShipMode = this.defaultShipMode.find(
        (el) => el.id === shipMode
      )!;
      const minTrips = this.currentShipMode.id;
      this.setShipMode(this.currentShipMode);
      const trips = this.trips;
      // this.setTrips([]);
      if (trips.length < minTrips) {
        for (let i = trips.length; i < minTrips; i++) {
          this.addTrip();
        }
      } else if (trips.length > minTrips) {
        for (let i = trips.length; i > minTrips; i--) {
          this.removeTrip();
        }
      }
      this.rendered = true;
    },
    onChangeBoxes(boxes: number) {
      const nBoxes = boxes;
      if (this.boxes.length < nBoxes) {
        for (let i = this.boxes.length; i < nBoxes; i++) {
          this.setBoxes([...this.boxes, new Box()]);
        }
      } else if (this.boxes.length > nBoxes) {
        for (let i = this.boxes.length; i > nBoxes; i--) {
          this.removeBoxes();
        }
      }
    },
    onCountryChanged(country: any) {
      const index = country.index;
      this.currentCountries[index] = country.shortName;
      (this as any).validation.module.validateBoxes();
      (this as any).validation.module.validateStages();
      (this as any).validation.module.validateIsInternationalAndEUEnabled();
    },
    purposeSelectionMade(selection: any) {
      if (selection) {
        this.purposeSelected = selection.option;
      } else {
        this.purposeSelected = false;
      }
    },
    setError(e, type: any = "") {
      // example: called from stageForm e = {direction: 'from' || 'to', country: 'Russia'}, type = 'country'
      // call to request error on validation object
      (this as any).validation.module.requestAlert({ event: e, type });
    },
    clearError(e, type: any = "") {
      // example: called from stageForm e = 'from' || 'to', type = 'country'
      (this as any).validation.module.removeNotice({
        event: { direction: e },
        type,
      });
      // call to clean individual error on validation object
    },
    validateBoxes() {
      // call validate boxes in validation object
      (this as any).validation.module.validateBoxes();
      this.recomputeNudges();
    },
    recomputeNudges() {
      const newConfirmations: Record<string, boolean> = {};
      for (let i = 0; i < this.boxes.length; i++) {
        // Iterate every active alert on the box so each stacked alert keeps its checks.
        this.getAllChecksForBox(i).forEach(c => {
          newConfirmations[c.id] = this.nudgeConfirmations[c.id] || false;
        });
        // Protection alert check keys (independent system)
        const protectionChecks = this.getProtectionChecks(i);
        protectionChecks.forEach((c: any) => {
          newConfirmations[c.id] = this.nudgeConfirmations[c.id] || false;
        });
      }
      this.nudgeConfirmations = newConfirmations;
    },
    onNudgeConfirm(nudgeId: string, value: boolean) {
      this.$set(this.nudgeConfirmations, nudgeId, value);
    },

    getGuidanceCheckLabels(box: any, alert?: { state: string; tier: string; metric: string }): string[] {
      const a = alert ?? this.getBoxThresholdTier(box);
      const { state, tier, metric } = a;
      if (state === 'safe' || !tier) return [];
      const key = this.getGuidanceAlertKey(tier, state, metric);
      const notice = this.guidanceAlerts[key];
      if (!notice?.message) return [];
      const replacements = this.getGuidanceReplacements(box, a);
      return extractCheckTags(notice.message, replacements);
    },
    // Checks for a specific alert (used in the template's per-alert render)
    getChecksForAlert(boxIndex: number, alert: { state: string; tier: string; metric: string }): Array<{ id: string; label: string }> {
      const alertKey = this.getGuidanceAlertKey(alert.tier, alert.state, alert.metric);
      const labels = this.getGuidanceCheckLabels(this.boxes[boxIndex], alert);
      return labels.map((label, ci) => ({
        id: `guidance-${boxIndex}-${alertKey}:${ci}`,
        label,
      }));
    },
    // Aggregate of all checks across all active alerts on the box.
    // Used for global state (recomputeNudges + allNudgesConfirmed).
    getAllChecksForBox(boxIndex: number): Array<{ id: string; label: string }> {
      const checks: Array<{ id: string; label: string }> = [];
      const alerts = this.getActiveAlerts(this.boxes[boxIndex]);
      alerts.forEach(alert => {
        checks.push(...this.getChecksForAlert(boxIndex, alert));
      });
      return checks;
    },
    // Per-alert details mode (so each stacked alert manages its own collapse state).
    getDetailsModeForAlert(boxIndex: number, alert: { state: string; tier: string; metric: string }): 'collapsible' | 'locked' | 'hidden' {
      const checks = this.getChecksForAlert(boxIndex, alert);
      if (checks.length === 0) {
        // No checkbox case (e.g. danger OVR hard stop): body always visible,
        // no Hide button — user must read the hard stop, Search is blocked anyway.
        return 'locked';
      }
      const allConfirmed = checks.every(c => this.nudgeConfirmations[c.id] === true);
      return allConfirmed ? 'hidden' : 'locked';
    },
    // True when an alert has confirmable checkboxes and all of them are checked.
    // Per Bill: once confirmed, hide the badge/expletive too to reduce clutter
    // (the body is already collapsed via getDetailsModeForAlert returning 'hidden').
    isAlertConfirmed(boxIndex: number, alert: { state: string; tier: string; metric: string }): boolean {
      const checks = this.getChecksForAlert(boxIndex, alert);
      if (checks.length === 0) return false;
      return checks.every(c => this.nudgeConfirmations[c.id] === true);
    },
    // Per-field shading. Returns '' (no border) for fields with no applicable
    // warn/danger alert, else 'warn'/'danger'. Only orange/red borders are shown
    // — there's no green "safe" border (LPS-danger "Large Package" is a valid
    // band and intentionally produces no border).
    getFieldShading(boxIndex: number): Record<'length' | 'width' | 'height' | 'weight', '' | 'warn' | 'danger'> {
      const result = { length: '', width: '', height: '', weight: '' } as Record<'length' | 'width' | 'height' | 'weight', '' | 'warn' | 'danger'>;
      const box = this.boxes[boxIndex];
      if (!box || !this.hasAllSpatialDims(box) || !this.toNum(box?.weight)) return result;
      const alerts = this.getDebouncedAlerts(boxIndex);
      if (!alerts.length) return result;

      const severity = (s: '' | 'warn' | 'danger') => (s === 'danger' ? 2 : s === 'warn' ? 1 : 0);
      const bump = (field: 'length' | 'width' | 'height' | 'weight', next: 'warn' | 'danger') => {
        if (severity(next) > severity(result[field])) result[field] = next;
      };

      for (const alert of alerts) {
        // LPS-danger = Large Package, valid band — no border.
        if (alert.state === 'danger' && alert.tier === 'lps') continue;
        const tier = this.resolveFieldShadeFromAlert(alert);
        if (tier === 'safe') continue;
        if (alert.tier === 'scicon' || alert.tier === 'min') {
          bump('length', tier); bump('width', tier); bump('height', tier);
          continue;
        }
        if (alert.metric === 'weight' || alert.tier === 'heavy') {
          bump('weight', tier);
          continue;
        }
        if (alert.metric === 'lg' || alert.metric === 'cubic') {
          bump('length', tier); bump('width', tier); bump('height', tier);
          continue;
        }
        if (alert.metric === 'length') {
          // Longest-side rule: only the side(s) equal to the max get the color.
          const l = this.toNum(box?.length);
          const w = this.toNum(box?.width);
          const h = this.toNum(box?.height);
          const max = Math.max(l, w, h);
          if (l === max) bump('length', tier);
          if (w === max) bump('width', tier);
          if (h === max) bump('height', tier);
          continue;
        }
        if (alert.metric === 'width') bump('width', tier);
      }
      return result;
    },
    // Resolves shading tier from the admin alert `type` in DB (source of truth),
    // falling back to the alert's own state when the row is missing.
    resolveFieldShadeFromAlert(alert: { state: string; tier: string; metric: string }): 'safe' | 'warn' | 'danger' {
      const key = this.getGuidanceAlertKey(alert.tier, alert.state, alert.metric)
        || (alert.tier === 'scicon' ? 'SciconWarning' : alert.tier === 'min' ? 'AlertMinimalSize' : '');
      const type = key ? this.guidanceAlerts[key]?.type : undefined;
      if (type === 'alert-danger') return 'danger';
      if (type === 'alert-warning') return 'warn';
      if (type === 'alert-success' || type === 'alert-info') return 'safe';
      // DB miss → fall back to the alert's own state. 'at' is rendered like warn.
      if (alert.state === 'danger') return 'danger';
      if (alert.state === 'warn' || alert.state === 'at') return 'warn';
      return 'safe';
    },
    getGuidanceAlertKey(tier: string, state: string, metric: string): string {
      if (tier === 'ovr') {
        if (state === 'danger') {
          if (metric === 'lg') return 'AlertOverSizeLimit';
          if (metric === 'length') return 'AlertOverDimLimit';
          return 'AlertOverWeightLimit';
        }
        if (state === 'at') {
          if (metric === 'lg') return 'AlertAtSizeLimit';
          if (metric === 'length') return 'AlertAtDimLimit';
          return 'AlertAtWeightLimit';
        }
        if (metric === 'lg') return 'AlertNearSizeLimit';
        if (metric === 'length') return 'AlertNearDimLimit';
        return 'AlertNearWeightLimit';
      }
      if (tier === 'lps') {
        // Danger LPS (>130 L+G or >17,280 cubic): badge only, no body alert
        if (state === 'danger') return '';
        if (state === 'at') {
          if (metric === 'length') return 'AlertAtLPS-L';
          if (metric === 'lg') return 'AlertAtLPS-LG';
          if (metric === 'cubic') return 'AlertAtLPS-Cubic';
        }
        // near zone
        if (metric === 'length') return 'AlertNearLPS-L';
        if (metric === 'lg') return 'AlertNearLPS-LG';
        if (metric === 'cubic') return 'AlertNearLPS-Cubic';
        return '';
      }
      if (tier === 'heavy') {
        if (metric === 'sturdy') return 'AlertHeavyPackage-Sturdy';
      }
      if (tier === 'scicon') return 'SciconWarning';
      if (tier === 'min') return 'AlertMinimalSize';
      return '';
    },
    getGuidanceReplacements(box: any, alert?: { state: string; tier: string; metric: string }): Record<string, string> {
      const u = this.getUnits();
      const t = this.getAllThresholds();
      const a = alert ?? this.getBoxThresholdTier(box);
      const { state, tier, metric } = a;
      if (state === 'safe' || !tier) return {};
      const LG = this.calcLG(box);
      const VOL = this.calcCubic(box);
      const L = Math.max(this.toNum(box?.length), this.toNum(box?.width), this.toNum(box?.height));
      const W = this.toNum(box?.width);
      const WT = this.toNum(box?.weight);

      // Heavy tier uses weight against the OVR max as the "limit" reference for replacements
      let value = 0;
      let limit = 0;
      let unitLabel = u.length;
      if (tier === 'heavy') {
        value = WT;
        limit = t.ovr.weight;
        unitLabel = u.weight;
      } else {
        const tierThresholds = t[tier as keyof typeof t] as any;
        if (!tierThresholds) return {};
        const valueMap: Record<string, number> = { lg: LG, cubic: VOL, length: L, width: W, weight: WT };
        value = valueMap[metric] || 0;
        limit = tierThresholds[metric] || 0;
        unitLabel = metric === 'weight' ? u.weight : metric === 'cubic' ? u.cubic : u.length;
      }
      const distance = Math.abs(limit - value);
      const thresholdName = tier === 'lps' ? 'Large Package Surcharge' : tier === 'heavy' ? 'Heavy Package' : 'Maximum';
      return {
        '[value]': metric === 'cubic' ? this.formatNumber(value) : String(value),
        '[limit]': metric === 'cubic' ? this.formatNumber(limit) : String(limit),
        '[distance]': metric === 'cubic' ? this.formatNumber(distance) : String(distance),
        '[unit]': unitLabel,
        '[metric]': metric,
        '[thresholdname]': thresholdName,
        // Aliases for admin convenience
        '[weight]': String(WT),
        '[length]': String(L),
        '[width]': String(W),
        '[height]': String(this.toNum(box?.height)),
        '[lg]': String(LG),
        '[cubic]': metric === 'cubic' ? this.formatNumber(VOL) : String(VOL),
      };
    },

    // -- Protection guidance methods --
    isProtectionTooLow(box: any): boolean {
      if (!box?.marketValue || !box?.premiumProtection) return false;
      const mv = Number(box.marketValue) || 0;
      const pp = Number(box.premiumProtection?.coverage ?? box.premiumProtection?.value ?? 0) || 0;
      return mv > 0 && pp > 0 && pp < mv;
    },
    getProtectionAlert(): NoticeEntity | null {
      return this.guidanceAlerts['AlertProtectionTooLow'] || null;
    },
    getProtectionReplacements(box: any): Record<string, string> {
      const mv = Number(box?.marketValue) || 0;
      const pp = Number(box?.premiumProtection?.coverage ?? box?.premiumProtection?.value ?? 0) || 0;
      return {
        '[protectionValue]': String(pp),
        '[difference]': String(mv - pp),
        '[marketvalue]': String(mv),
        '[value]': String(pp),
        '[distance]': String(mv - pp),
        '[protectionvalue]': String(pp),
      };
    },
    getProtectionBadgeText(box: any): string {
      const notice = this.getProtectionAlert();
      if (!notice) return 'Coverage below value';
      const replacements = this.getProtectionReplacements(box);
      // [pill="..."] tag wins as an explicit short-label override
      const pill = extractPillTag(notice.expletive || '', replacements)
        || extractPillTag(notice.message || '', replacements);
      if (pill) return pill;
      // Match the dim/weight alerts pattern (per Bill): use the admin expletive
      // as the badge label so we don't show "Coverage below value" + the expletive
      // stacked. Falls back to the hardcoded label only if the alert row is missing.
      if (notice.expletive) {
        return this.applyReplacements(notice.expletive, replacements);
      }
      return 'Coverage below value';
    },

    getProtectionDetailText(): string {
      const notice = this.getProtectionAlert();
      if (!notice?.message) return '';
      let text = notice.message;
      // Strip expletive prefix from message body if it duplicates the title
      if (notice.expletive && text.startsWith(notice.expletive)) {
        text = text.slice(notice.expletive.length).trimStart();
      }
      return text;
    },
    getProtectionChecks(boxIndex: number): Array<{ id: string; label: string }> {
      const box = this.boxes[boxIndex];
      if (!this.isProtectionTooLow(box)) return [];
      const notice = this.getProtectionAlert();
      if (!notice?.message) return [];
      const replacements = this.getProtectionReplacements(box);
      const labels = extractCheckTags(notice.message, replacements);
      const pp = box?.premiumProtection?.coverage ?? box?.premiumProtection?.value ?? 0;
      return labels.map((label: string, ci: number) => ({
        id: `protection-${boxIndex}-${pp}:${ci}`,
        label,
      }));
    },
    getProtectionDetailsMode(boxIndex: number): 'collapsible' | 'locked' | 'hidden' {
      const checks = this.getProtectionChecks(boxIndex);
      if (checks.length === 0) return 'collapsible';
      const allConfirmed = checks.every((c) => this.nudgeConfirmations[c.id] === true);
      return allConfirmed ? 'hidden' : 'locked';
    },
    isProtectionConfirmed(boxIndex: number): boolean {
      const checks = this.getProtectionChecks(boxIndex);
      if (checks.length === 0) return false;
      return checks.every((c) => this.nudgeConfirmations[c.id] === true);
    },


    // -- Dimension threshold helper methods --
    hasAnyDimension(box: any): boolean {
      if (!box) return false;
      return !!(box.length || box.width || box.height || box.weight);
    },
    hasAllSpatialDims(box: any): boolean {
      if (!box) return false;
      return !!(box.length && box.width && box.height);
    },
    hasAllDimensions(box: any): boolean {
      if (!box) return false;
      return !!(box.length && box.width && box.height && box.weight);
    },
    toNum(v: any): number {
      const n = Number(v);
      return Number.isFinite(n) ? n : 0;
    },
    calcLG(box: any): number {
      const dims = [this.toNum(box?.length), this.toNum(box?.width), this.toNum(box?.height)];
      const longest = Math.max(...dims);
      const girth = dims.reduce((sum, d) => sum + d, 0) - longest;
      return longest + (2 * girth);
    },
    calcCubic(box: any): number {
      return this.toNum(box?.length) * this.toNum(box?.width) * this.toNum(box?.height);
    },
    formatNumber(n: number): string {
      return n.toLocaleString("en-US");
    },
    getUnits() {
      const isMetric = this.currentUnit?.length === "cm";
      return {
        length: isMetric ? "cm" : "in",
        weight: isMetric ? "kg" : "lb",
        cubic: isMetric ? "cm\u00B3" : "in\u00B3",
        isMetric,
      };
    },
    getAllThresholds() {
      const u = this.getUnits();
      if (u.isMetric) {
        return {
          lps: {
            lg: 330, nearLg: 320,
            cubic: 283168, nearCubic: 245806,
            length: 244, nearLength: 236,
          },
          ovr: {
            lg: 419, weight: 68, length: 274,
            nearLg: 406, nearWeight: 66, nearLength: 264,
          },
          heavy: {
            sturdyMin: 32, sturdyMax: 68,
          },
          min: {
            lg: 41, // 16 in ≈ 40.64 cm — round up to keep parity w/ imperial.
          },
        };
      }
      return {
        lps: {
          lg: 130, nearLg: 126,
          cubic: 17280, nearCubic: 15000,
          length: 96, nearLength: 93,
        },
        ovr: {
          lg: 165, weight: 150, length: 108,
          nearLg: 160, nearWeight: 145, nearLength: 104,
        },
        heavy: {
          sturdyMin: 70, sturdyMax: 150,
        },
        min: {
          lg: 16, // packages with L+G below 16 in fall under carrier minimums.
        },
      };
    },
    // Active alerts for the box. Three independent lanes: Length, LPS-volume
    // (Scicon > MinimalSize > L+G > Cubic), and Weight. Within each lane the
    // most severe alert wins (danger > at > warn).
    getActiveAlerts(box: any): Array<{ state: string; tier: string; metric: string }> {
      const t = this.getAllThresholds();
      const L = Math.max(this.toNum(box?.length), this.toNum(box?.width), this.toNum(box?.height));
      const WT = this.toNum(box?.weight);
      const allSpatial = this.hasAllSpatialDims(box);
      const LG = allSpatial ? this.calcLG(box) : 0;
      const VOL = allSpatial ? this.calcCubic(box) : 0;

      const alerts: Array<{ state: string; tier: string; metric: string }> = [];

      if (allSpatial) {
        // MinimalSize is a hard No that short-circuits the other dim lanes
        // (the box is too small to hit any LPS/OVR threshold anyway).
        if (LG > 0 && LG < t.min.lg) {
          alerts.push({ state: "danger", tier: "min", metric: "lg" });
        } else {
          // --- Length lane ---
          let lengthAlert: { state: string; tier: string; metric: string } | null = null;
          if (L > t.ovr.length) lengthAlert = { state: "danger", tier: "ovr", metric: "length" };
          else if (L === t.ovr.length) lengthAlert = { state: "at", tier: "ovr", metric: "length" };
          else if (L >= t.ovr.nearLength) lengthAlert = { state: "warn", tier: "ovr", metric: "length" };
          else if (L > t.lps.length) lengthAlert = { state: "danger", tier: "lps", metric: "length" };
          else if (L === t.lps.length) lengthAlert = { state: "at", tier: "lps", metric: "length" };
          else if (L >= t.lps.nearLength) lengthAlert = { state: "warn", tier: "lps", metric: "length" };
          if (lengthAlert) alerts.push(lengthAlert);

          // LPS-volume lane: Scicon (hard No) > L+G > Cubic (fallback).
          const isScicon = typeof box?.preset === 'string' && box.preset.toLowerCase().includes('scicon');
          if (isScicon) {
            alerts.push({ state: "danger", tier: "scicon", metric: "scicon" });
          } else {
            let lgAlert: { state: string; tier: string; metric: string } | null = null;
            if (LG > t.ovr.lg) lgAlert = { state: "danger", tier: "ovr", metric: "lg" };
            else if (LG === t.ovr.lg) lgAlert = { state: "at", tier: "ovr", metric: "lg" };
            else if (LG >= t.ovr.nearLg) lgAlert = { state: "warn", tier: "ovr", metric: "lg" };
            else if (LG > t.lps.lg) lgAlert = { state: "danger", tier: "lps", metric: "lg" };
            else if (LG === t.lps.lg) lgAlert = { state: "at", tier: "lps", metric: "lg" };
            else if (LG >= t.lps.nearLg) lgAlert = { state: "warn", tier: "lps", metric: "lg" };

            if (lgAlert) {
              alerts.push(lgAlert);
            } else {
              let cubicAlert: { state: string; tier: string; metric: string } | null = null;
              if (VOL > t.lps.cubic) cubicAlert = { state: "danger", tier: "lps", metric: "cubic" };
              else if (VOL === t.lps.cubic) cubicAlert = { state: "at", tier: "lps", metric: "cubic" };
              else if (VOL >= t.lps.nearCubic) cubicAlert = { state: "warn", tier: "lps", metric: "cubic" };
              if (cubicAlert) alerts.push(cubicAlert);
            }
          }
        }
      }

      // --- Weight-based alert (severity within weight: danger > at > warn) ----
      // Per Bill's updated rules: Heavy Package covers 70-150 lb as a single band
      // (no photos in this range). OVR near/at/danger intercepts 145+ first.
      let wt: { state: string; tier: string; metric: string } | null = null;
      if (WT > t.ovr.weight) wt = { state: "danger", tier: "ovr", metric: "weight" };
      else if (WT === t.ovr.weight) wt = { state: "at", tier: "ovr", metric: "weight" };
      else if (WT >= t.ovr.nearWeight) wt = { state: "warn", tier: "ovr", metric: "weight" };
      else if (WT >= t.heavy.sturdyMin && WT <= t.heavy.sturdyMax) wt = { state: "warn", tier: "heavy", metric: "sturdy" };
      if (wt) alerts.push(wt);

      return alerts;
    },

    // Legacy wrapper — returns the primary (most-severe-by-position) alert.
    // Kept so existing call sites (debouncing, field shading, hasOverMaxBox-ish
    // lookups that only need the dominant state) keep working unchanged.
    getBoxThresholdTier(box: any): { state: string; tier: string; metric: string } {
      const alerts = this.getActiveAlerts(box);
      return alerts[0] ?? { state: "safe", tier: "", metric: "" };
    },
    getDebouncedTier(boxIndex: number): { state: string; tier: string; metric: string } {
      return this.debouncedTiers[boxIndex] || { state: "safe", tier: "", metric: "" };
    },
    getDebouncedAlerts(boxIndex: number): Array<{ state: string; tier: string; metric: string }> {
      return this.debouncedAlerts[boxIndex] || [];
    },
    // Alerts to render. LPS-danger is excluded; that state surfaces via the
    // header label color (getMetricLabelClass) and field shading instead.
    getRenderableAlerts(boxIndex: number): Array<{ state: string; tier: string; metric: string }> {
      return this.getDebouncedAlerts(boxIndex).filter(
        a => !(a.state === "danger" && a.tier === "lps")
      );
    },
    // Color of the L+G / Cubic labels in the box header. Each label reflects its
    // own metric vs thresholds; the color is derived from the admin alert `type`
    // in DB so backoffice tweaks cascade automatically. Evaluates raw value (not
    // getActiveAlerts) so Cubic still colors itself when L+G suppresses it.
    getMetricLabelClass(boxIndex: number | string, metric: 'lg' | 'cubic'): string {
      const idx = Number(boxIndex);
      const box = this.boxes[idx];
      if (!box || !this.hasAllSpatialDims(box)) return 'text-guidance-verified';
      const t = this.getAllThresholds();

      // Resolve the alert key that applies to this metric's current zone.
      let alertKey = '';
      if (metric === 'lg') {
        const isScicon = typeof box.preset === 'string' && box.preset.toLowerCase().includes('scicon');
        if (isScicon) {
          alertKey = 'SciconWarning';
        } else {
          const LG = this.calcLG(box);
          if (LG <= 0) return 'text-guidance-verified';
          if (LG < t.min.lg) alertKey = 'AlertMinimalSize';
          else if (LG > t.ovr.lg) alertKey = this.getGuidanceAlertKey('ovr', 'danger', 'lg');
          else if (LG === t.ovr.lg) alertKey = this.getGuidanceAlertKey('ovr', 'at', 'lg');
          else if (LG >= t.ovr.nearLg) alertKey = this.getGuidanceAlertKey('ovr', 'warn', 'lg');
          else if (LG === t.lps.lg) alertKey = this.getGuidanceAlertKey('lps', 'at', 'lg');
          else if (LG >= t.lps.nearLg && LG < t.lps.lg) alertKey = this.getGuidanceAlertKey('lps', 'warn', 'lg');
          // LPS danger (LG > lps.lg) → Large Package, valid, no alert → green default.
        }
      } else {
        // cubic — only LPS thresholds apply.
        const VOL = this.calcCubic(box);
        if (VOL <= 0) return 'text-guidance-verified';
        if (VOL === t.lps.cubic) alertKey = this.getGuidanceAlertKey('lps', 'at', 'cubic');
        else if (VOL >= t.lps.nearCubic && VOL < t.lps.cubic) alertKey = this.getGuidanceAlertKey('lps', 'warn', 'cubic');
        // LPS danger (VOL > lps.cubic) → Large Package, valid, no alert → green default.
      }

      if (!alertKey) return 'text-guidance-verified';
      const notice = this.guidanceAlerts[alertKey];
      const type = notice?.type;
      if (type === 'alert-danger') return 'text-guidance-danger-text';
      if (type === 'alert-warning') return 'text-guidance-warn-text';
      if (type === 'alert-success' || type === 'alert-info') return 'text-guidance-verified';
      // DB miss → fallback to static zone color (matches the source-of-truth
      // expectations described in the threshold matrix).
      if (alertKey === 'AlertMinimalSize' || alertKey === 'SciconWarning') return 'text-guidance-danger-text';
      if (alertKey.startsWith('AlertOver')) return 'text-guidance-danger-text';
      return 'text-guidance-warn-text';
    },
    getBoxBadgeState(box: any, alert?: { state: string; tier: string; metric: string }): string {
      const a = alert ?? this.getBoxThresholdTier(box);
      // OVR at exact threshold: same orange color as warn (border state, not danger)
      if (a.state === 'at') return 'warn';
      return a.state;
    },
    // Returns the admin-configured `type` of the active alert so GuidanceBadge can
    // derive color + icon from it (per Bill's "assign icon to Type selected" request).
    // Empty string means "no admin alert applies" — badge falls back to state-based logic.
    getBoxBadgeType(box: any, alert?: { state: string; tier: string; metric: string }): string {
      const a = alert ?? this.getBoxThresholdTier(box);
      if (a.state === 'safe') return '';
      const key = this.getGuidanceAlertKey(a.tier, a.state, a.metric);
      const notice = this.guidanceAlerts[key];
      return notice?.type || '';
    },
    getBoxBadgeText(box: any, alert?: { state: string; tier: string; metric: string }): string {
      const a = alert ?? this.getBoxThresholdTier(box);
      const { state, tier, metric } = a;

      const key = this.getGuidanceAlertKey(tier, state, metric);
      const notice = this.guidanceAlerts[key];
      const replacements = this.getGuidanceReplacements(box, a);

      // [pill="..."] tag wins as an explicit short-label override
      const pill = extractPillTag(notice?.expletive || '', replacements)
        || extractPillTag(notice?.message || '', replacements);
      if (pill) return pill;

      // Use the admin-authored expletive as the badge label (per Bill's request:
      // "use the Expletive in Admin as that label"). Falls through to hardcoded
      // labels only when the alert row is missing from the DB.
      if (notice?.expletive) {
        return this.applyReplacements(notice.expletive, replacements);
      }

      // Fallback hardcoded labels (alert missing from DB)
      if (state === "danger") {
        if (tier === "scicon") return "Scicon Soft-Sided Case";
        if (tier === "min") return "Below Minimum Size";
        if (metric === "lg") return "Over Maximum Size";
        if (metric === "length") return "Over Maximum Length";
        return "Over Maximum Weight";
      }
      if (tier === "heavy") return "Heavy Package";
      if (state === "at" && tier === "ovr") {
        if (metric === "lg") return "At Maximum Size";
        if (metric === "length") return "At Maximum Length";
        return "At Maximum Weight";
      }
      if (tier === "ovr") {
        if (metric === "lg") return "Near Maximum Size";
        if (metric === "length") return "Near Maximum Length";
        return "Near Maximum Weight";
      }
      if (metric === "cubic") return "Near Large Package";
      return "Near Large Package (L+G)";
    },
    applyReplacements(text: string, replacements: Record<string, string>): string {
      let result = text;
      for (const [key, value] of Object.entries(replacements)) {
        result = result.replaceAll(key, value);
      }
      return result;
    },
    getBoxDetailText(box: any, alert?: { state: string; tier: string; metric: string }): string {
      const a = alert ?? this.getBoxThresholdTier(box);
      const { state, tier, metric } = a;
      if (state === "safe") return "";
      const key = this.getGuidanceAlertKey(tier, state, metric);
      const notice = this.guidanceAlerts[key];
      if (notice?.message) return this.applyReplacements(notice.message, this.getGuidanceReplacements(box, a));
      return "";
    },
    getBoxTip(box: any, alert?: { state: string; tier: string; metric: string }): string {
      const a = alert ?? this.getBoxThresholdTier(box);
      const { state, tier, metric } = a;
      if (metric === "weight" || tier === "heavy") return `Please ${state === "danger" ? "confirm" : "weigh"} the fully packed shipment and round up to the next ${this.getUnits().weight === "lb" ? "pound" : "kilogram"}.`;
      if (state === "danger") return "Please confirm measurements include all protrusions and are rounded up.";
      return `Please measure the outermost points of the packed box, bag, or case and round up to the next ${this.getUnits().length === "in" ? "inch" : "centimeter"}.`;
    },

    async validateStep() {
      await new Promise((resolve) => {
        setTimeout(() => {
          resolve(false);
        }, 100);
      });
      if (await (this as any).validation.Validate()) {
        // Recompute nudges with final validated values
        this.recomputeNudges();
        // Block submission if nudges are unconfirmed
        if (!this.allNudgesConfirmed) {
          return;
        }
        // Reassign to clean any active search
        this.setLoading(true);
        this.reassignActiveSearch();
        if (this.checkIfNeededAVSModal()) {
          this.setLoading(false);
          this.isOpen = true;
          this.markAvsChecked();
          return;
        }
        this.markAvsChecked();
        this.setDetails(this.stageDetails);
        this.logRequest(window ? window.navigator.userAgent : "");
        this.requestRates({ stepName: "Costs", stepNumber: 2 });
      }
    },
    onSearchAbandoned(data) {
      this.forgetSearch(data);
    },
    onSearchResumed(data) {
      this.currentUnit = data.search.units;
      this.$nextTick(() => this.resumeSearch(data));
      this.logRequest(window ? window.navigator.userAgent : "");
    },
    async getUserPresets() {
      try {
        const _this = this as any;
        const response = await _this.requestUserPresets();
        if (response) {
          if (response.userPackages) {
            _this.userPresets = response.userPackages
              .map((el: UserPreset) => {
                // sort boxes
                return {
                  label: el.name,
                  length: el.length,
                  width: el.width,
                  height: el.height,
                  weight: el.weight,
                  isEbike: el.isEbike,
                  linearUnit: el.linearUnit,
                  weightUnit: el.weightUnit,
                  hasBattery: el.hasBattery,
                  coverage: el.coverage,
                  description: el.description,
                  outsideDescription: el.outsideDescription,
                  categoryCode: el.categoryCode,
                  marketValue: el.marketValue,
                };
              })
              .sort((a, b) => {
                return a.sortOrder < b.sortOrder
                  ? -1
                  : a.sortOrder > b.sortOrder
                  ? 1
                  : 0;
              });
          }
          if (response.userAddresses) {
            // no need to sort addresses
            _this.userAddresses = response.userAddresses;
          }
        } else {
          _this.userPresets = [] as UserPreset[];
          _this.userAddresses = [] as SavedAddress[];
        }
      } catch (e) {
        Sentry.captureException(e);
      }
    },
    setUpdatedDetails(
      {
        updatedDetails,
        direction,
        changedFlags,
      }: {
        updatedDetails: Details;
        direction: string;
        changedFlags: any;
      },
      index: number
    ) {
      if (!this.updatedDetails?.[index]) {
        this.updatedDetails[index] = { from: new Details(), to: new Details() };
      }

      if (!this.changedFlags[index]) {
        this.changedFlags[index] = { from: {}, to: {} };
      }
      this.changedFlags[index][direction] = changedFlags;
      this.updatedDetails[index][direction] = updatedDetails;
    },
    hideModal() {
      this.isOpen = false;
      this.validation.module.validating = false;
      this.avsChecked = true;
      this.markAvsChecked();
      this.setRejectedEnumState();
      this.setDetails(this.stageDetails);
      this.changedFlags = [];
      // BIK-1995: top banner removed — AVS failure now shown inline via avsMessage("apiFailed")
      // this.requestAlert({ name: "warningAVSFailed", type: "address" });
    },
    onlyCloseModal() {
      this.isOpen = false;
    },
    setRejectedEnumState() {
      for (let i = 0; i < this.stageDetails.length; i++) {
        const stageDetail = this.stageDetails[i];
        const fromHadChanges = !areAllPropertiesFalse(
          this.changedFlags?.[i]?.from
        );
        const toHadChanges = !areAllPropertiesFalse(
          this.changedFlags?.[i]?.to
        );
        if (!stageDetail.from.isSavedAddress && fromHadChanges) {
          stageDetail.from.avsStatus.status = AVSStatus.failed;
          stageDetail.from.avsStatus.subStatus = AVSSubStatus.rejectedByUser;
          stageDetail.from.passedAvs = false;
        }
        if (!stageDetail.to.isSavedAddress && toHadChanges) {
          stageDetail.to.avsStatus.status = AVSStatus.failed;
          stageDetail.to.avsStatus.subStatus = AVSSubStatus.rejectedByUser;
          stageDetail.to.passedAvs = false;
        }
      }
    },
    completeModal() {
      if (this.isOpen) {
        this.setLoading(true);
        this.updateStage(true);
        this.isOpen = false;
        const stageDetailsUpdated = _.cloneDeep(this.stageDetails);
        for (const stageDetail of stageDetailsUpdated) {
          stageDetail.from.avsChecked = true;
          stageDetail.to.avsChecked = true;
        }
        this.setDetails(stageDetailsUpdated);
        this.changedFlags = [];
        this.logRequest(window ? window.navigator.userAgent : "");
        this.requestRates({ stepName: "Costs", stepNumber: 2 });
      }
      (this as any).validation.module.validating = false;
    },
    updateStage(avsFailed = true) {
      // here, check here if it is being added
      const updateProperty = (source, target, property) => {
        if (typeof source[property] === "string") {
          target[property] = source[property].trim();
        } else if (source[property] !== undefined) {
          target[property] = source[property];
        }
      };
      for (let step = 0; step < (this as any).stageDetails.length; step++) {
        const stageDetail = this.stageDetails[step];
        const updatedDetail = this.updatedDetails[step];
        const properties = [
          "company",
          "streetAddress",
          "address1",
          "address2",
          "address3",
          "city",
          "state",
          "postalCode",
          "countryCode",
          "isResidential",
          "success",
          "passedAvs",
          "errorCode",
          "errorMessage",
          "isChanged",
          "validationLevel",
          "validationSource",
          "avsStatus",
          "avsSubStatus",
          "avsStatus",
        ];
        if (avsFailed && stageDetail && updatedDetail) {
          ["from", "to"].forEach((key) => {
            const isValidSavedAddress =
              stageDetail[key].isSavedAddress && stageDetail[key].passedAvs;
            const isValidEventAddress =
              stageDetail[key].isEventAddress && stageDetail[key].passedAvs;
            if (
              stageDetail[key] &&
              updatedDetail[key] &&
              !isValidSavedAddress &&
              !isValidEventAddress
            ) {
              properties.forEach((property) =>
                updateProperty(updatedDetail[key], stageDetail[key], property)
              );
            }
          });
        } else if (updatedDetail.from && stageDetail.from) {
          properties.forEach((property) =>
            updateProperty(stageDetail.from, updatedDetail.from, property)
          );
        }
      }
    },
    mapStates(states: Array<State>) {
      try {
        const _this = this;
        for (let step = 0; step < states.length; step++) {
          const country = states[step].country;
          const countryStates = _this.allowedStates[country];
          if (
            typeof countryStates === "undefined" ||
            (countryStates && countryStates.length === 0)
          ) {
            _this.allowedStates[country] = [];
          }
          const state = {} as State;
          Object.assign(state, states[step]);
          state.id = state.stateprovcode;
          state.label = state.stateprovcode + " - " + state.stateprov;
          _this.allowedStates[country].push(state);
        }
        return true;
      } catch (e) {
        return false;
      }
    },
    markAvsChecked() {
      this.stageDetails = _.cloneDeep(this.details);
      for (const stageDetail of this.stageDetails) {
        stageDetail.from.avsChecked = true;
        stageDetail.to.avsChecked = true;
      }
    },
    checkIfNeededAVSModal(): boolean {
      let needsAVSModal = false;
      for (let i = 0; i < this.details.length; i++) {
        if (
          this.details[i].from.streetAddress ||
          this.details[i].from.address1
        ) {
          if (
            !areAllPropertiesFalse(this.changedFlags?.[i]?.from)
            && this.hasRealAddressDiff(
              this.details[i].from,
              this.updatedDetails?.[i]?.from,
            )
          ) {
            needsAVSModal = true;
          }
        }
        if (this.details[i].to.streetAddress || this.details[i].to.address1) {
          if (
            !areAllPropertiesFalse(this.changedFlags?.[i]?.to)
            && this.hasRealAddressDiff(
              this.details[i].to,
              this.updatedDetails?.[i]?.to,
            )
          ) {
            needsAVSModal = true;
          }
        }
      }
      return needsAVSModal;
    },
    hasRealAddressDiff(userEntered: any, avsReturned: any): boolean {
      // Defensive: backend AVS occasionally marks isAddress2Changed=true (or other
      // flags) when both sides are byte-identical. Compare normalized strings here
      // so the modal only opens when something actually differs.
      if (!userEntered || !avsReturned) return false;
      const norm = (v: any): string => {
        if (v == null) return "";
        if (typeof v === "string") return v.trim().toUpperCase();
        if (typeof v === "object") {
          return String(
            v.stateprovcode ?? v.code ?? v.label ?? ""
          ).trim().toUpperCase();
        }
        return String(v).trim().toUpperCase();
      };
      const fields = [
        "company",
        "streetAddress",
        "address1",
        "address2",
        "city",
        "state",
        "postalCode",
        "country",
      ];
      return fields.some((f) => norm(userEntered[f]) !== norm(avsReturned[f]));
    },
    // BIK-2137: the state a user entered, as a display string. The modal's "You
    // Entered" columns tested the state OBJECT for truthiness and then printed
    // `.stateprovcode`, so an international address — which carries an empty
    // placeholder object rather than no state at all — rendered an orphan comma
    // ("Sesimbra , 2970-277 PT"). Resolve the value first, and accept a plain string
    // too, mirroring how the "Updated" column and hasRealAddressDiff already handle
    // both shapes. Returns "" when there is nothing to show, so the v-if hides it.
    enteredState(detail: any): string {
      const state = detail?.state;
      if (!state) return "";
      if (typeof state === "string") return state;
      return state.stateprovcode ?? "";
    },
    disabledContinueBtn(event) {
      this.disabled = event;
    },

    checkIfDetailUpdated,
    areAllPropertiesFalse,
  },
  fetchOnServer: false,
});
